import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';

import { CURRENCY_CONFIG, GAME_TAG_COLORS } from '@/constants/constant';
import { EStatus, type ECurrency } from '@/enum';

export const getGameTagColorClass = (index: number): string =>
  GAME_TAG_COLORS[index % GAME_TAG_COLORS.length];

export const getTodayIso = () => dayjs().format('YYYY-MM-DD');
export const getNextDayIso = (iso: string) =>
  dayjs(iso).add(1, 'day').format('YYYY-MM-DD');
export const getPrevDayIso = (iso: string) =>
  dayjs(iso).subtract(1, 'day').format('YYYY-MM-DD');

/**
 * Formats a subscription amount with the correct symbol, position, and decimals.
 * USD → "$123.45"  (prefix, 2 decimals)
 * KWD → "123.456 د.ك"  (suffix, 3 decimals)
 */
export const formatCurrency = (
  amount: number | string,
  currency: ECurrency
): string => {
  const config = CURRENCY_CONFIG[currency];
  const formatted = Number(amount).toFixed(config.decimals);
  return config.prefix
    ? `${config.symbol}${formatted}`
    : `${formatted} ${config.symbol}`;
};

/** Flatten nested JSON messages for react-intl */
export const flattenMessages = (
  obj: Record<string, unknown>,
  prefix = ''
): Record<string, string> => {
  return Object.keys(obj).reduce(
    (acc, key) => {
      const value = obj[key];
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(
          acc,
          flattenMessages(value as Record<string, unknown>, newKey)
        );
      } else {
        acc[newKey] = String(value);
      }
      return acc;
    },
    {} as Record<string, string>
  );
};

function setCookie(name: string, value: string, days = 365) {
  const maxAge = days * 24 * 60 * 60; // seconds
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function getCookie(name: string) {
  const cookieValue = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
  return cookieValue ? cookieValue.pop() : '';
}

export function generateOrGetUserId() {
  let userId = getCookie('device_id');
  if (!userId) {
    userId = uuidv4();
    setCookie('device_id', userId);
  }
  return userId;
}

export const pickBy = (
  obj: Record<string, unknown>,
  predicate: (value: unknown, key: string) => boolean
) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => predicate(value, key))
  );
};

export const customPredicate = (value: unknown) =>
  value !== null && value !== undefined && value !== '';

export const formatQuery = (query: Record<string, unknown>): string => {
  if (!query || typeof query !== 'object') {
    return '';
  }

  const filteredQuery = pickBy(query, customPredicate);

  const buildQueryParams = (
    obj: Record<string, unknown>,
    parentKey = ''
  ): string[] => {
    return Object.entries(obj).flatMap(([key, value]) => {
      const fullKey = parentKey ? `${parentKey}[${key}]` : key;

      if (Array.isArray(value)) {
        return value.map(
          (item, index) =>
            `${fullKey}[${index}]=${encodeURIComponent(String(item))}`
        );
      } else if (value && typeof value === 'object') {
        return buildQueryParams(value as Record<string, unknown>, fullKey);
      } else {
        return `${fullKey}=${encodeURIComponent(String(value))}`;
      }
    });
  };

  return buildQueryParams(filteredQuery).join('&');
};

export function getStatusColorClass(status: string) {
  switch (status) {
    case EStatus.ACTIVE:
      return 'bg-success-light-50 text-success-secondary';
    case EStatus.COMPLETED:
      return 'bg-success-light-50 text-success-secondary';
    case EStatus.IN_PROGRESS:
      return 'bg-indigo-100 text-indigo-600';
    case EStatus.PENDING:
      return 'bg-warning-light-100 text-warning-primary';
    case EStatus.IN_ACTIVE:
      return 'bg-danger-light-100 text-danger-primary';
    default:
      return 'bg-neutral-light-100 text-neutral-secondary';
  }
}

export function getStatusLabel(status: string) {
  switch (status) {
    case EStatus.ACTIVE:
      return 'Active';
    case EStatus.COMPLETED:
      return 'Completed';
    case EStatus.IN_PROGRESS:
      return 'In Progress';
    case EStatus.PENDING:
      return 'Pending';
    case EStatus.IN_ACTIVE:
      return 'Inactive';
    default:
      return status;
  }
}

export const formatLastActive = (
  timestamp?: number,
  formatText?: (key: string, values?: Record<string, string | number>) => string
) => {
  if (!timestamp) return '-';

  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60)
    return formatText ? formatText('common.time.just-now') : 'Just now';

  const minutes = Math.floor(diff / 60);
  if (minutes < 60)
    return formatText
      ? formatText('common.time.minutes-ago', { value: minutes })
      : `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return formatText
      ? formatText('common.time.hours-ago', { value: hours })
      : `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 30)
    return formatText
      ? formatText('common.time.days-ago', { value: days })
      : `${days}d ago`;

  const months = Math.floor(days / 30);
  if (months < 12)
    return formatText
      ? formatText('common.time.months-ago', { value: months })
      : `${months}mo ago`;

  const years = Math.floor(months / 12);
  return formatText
    ? formatText('common.time.years-ago', { value: years })
    : `${years}y ago`;
};

/**
 * Convert a raw duration in seconds into a compact "Xd Yh", "Xh Ym", or "Xm" label.
 * Used for displaying screen time / usage values returned by the API in seconds.
 */
export const formatSecondsToDuration = (seconds?: number | string): string => {
  const total = Number(seconds);

  if (!Number.isFinite(total) || total <= 0) {
    return '0m';
  }

  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);

  const minutes = Math.round((total % 3600) / 60);

  if (days > 0) {
    return `${days}d${hours > 0 ? ` ${hours}h` : ''}`;
  }

  if (hours > 0) {
    return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
  }

  return `${minutes}m`;
};

export const getCurrentTimeInUnix = () => {
  const time = Math.floor(Date.now() / 1000);
  return time;
};

export const getStartOfMonthUnix = () => {
  const date = new Date();
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return Math.floor(date.getTime() / 1000);
};

export const getYearOptions = () => {
  const startYear = 2026;
  const currentYear = new Date().getFullYear();

  const years = [];
  for (let year = startYear; year <= currentYear; year++) {
    years.push({ label: year.toString(), value: year.toString() });
  }

  return years.reverse();
};

export const formatLastActiveAt = (unixTimestamp: number): string => {
  const date = new Date(unixTimestamp * 1000);
  const now = new Date();

  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  const time = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  if (isToday) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;

  const dateLabel = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return `${dateLabel}, ${time}`;
};

//unix to long date format
export const formatUnixToLongDate = (unixTimestamp: number): string => {
  const date = new Date(unixTimestamp * 1000);

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const stripHtml = (value: string) => {
  if (typeof value !== 'string') return value;

  try {
    const tmp = document.createElement('div');
    tmp.innerHTML = value;
    /* v8 ignore start */
    const rawText = tmp.textContent || tmp.innerText || '';
    /* v8 ignore stop */
    const text = rawText.replace(/\u00A0/g, ' ');
    const trimmed = text.trim();
    return trimmed === '' ? undefined : trimmed;
  } catch {
    // fallback
    const withoutTags = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
    const trimmed = withoutTags.replace(/\u00A0/g, ' ').trim();
    return trimmed === '' ? undefined : trimmed;
  }
};

export const downloadFile = async (blob: Blob, fileName: string) => {
  // Sanitize to avoid header injection or path-like values in the download name
  const safeFileName = fileName.replace(/[\r\n"]/g, '');
  let finalBlob = blob;
  if (safeFileName.endsWith('.csv')) {
    const text = await blob.text();
    finalBlob = new Blob(['\uFEFF' + text], {
      type: 'text/csv;charset=utf-8;',
    });
  }
  const url = window.URL.createObjectURL(finalBlob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', safeFileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getAssetUrl = (
  asset?:
    | string
    | {
        url?: string;
        base_url?: string;
        root?: string;
        folder?: string;
        name?: string;
      }
    | null
): string => {
  if (!asset) return '';
  if (typeof asset === 'string') return asset;
  // Backend may send the full URL in `url`; don't append root/folder/name again
  const isFullUrl =
    !!asset.url &&
    /^https?:\/\//.test(asset.url) &&
    (!asset.name || asset.url.endsWith(asset.name));
  if (isFullUrl) return asset.url as string;
  return `${asset.url || asset.base_url || ''}${asset.root || ''}${asset.folder || ''}${asset.name || ''}`;
};
